import { useCallback, useEffect, useRef, useState } from "react";

import {
    type LienWaiverInfo,
    type LienWaiverReviewResponse,
} from "entity/bill/LienWaiverReview/LienWaiverReview.api.types";
import { LienWaiverStatusTypes } from "entity/lienWaiver/LienWaiver/LienWaiver.api.types";

/**
 * Query param naming the waiver a sub just signed through Nutrient. The signing flow appends it to the
 * return URL so the review page knows an approval is in flight for that row.
 */
export const SIGNED_LIEN_WAIVER_PARAM = "signedLienWaiverId";

const POLL_INTERVAL_MS = 5000;

/**
 * Bounded so a sub never sits on an endless spinner. Nutrient approval normally lands within a couple of
 * seconds; past this the page tells them the approval was received and is still processing.
 */
const POLL_TIMEOUT_MS = 60_000;

/** A waiver has left the sub's queue once it is no longer awaiting their signature. */
export function isLienWaiverStillPending(lienWaiver: LienWaiverInfo | null | undefined) {
    return lienWaiver?.status === LienWaiverStatusTypes.Released;
}

function findLienWaiver(data: LienWaiverReviewResponse | undefined, encodedLienWaiverId: string) {
    return data?.allBills
        .flatMap((bill) => bill.lienWaivers)
        .find((lienWaiver) => String(lienWaiver.lienWaiverId) === encodedLienWaiverId);
}

export type SignedLienWaiverPollState = "idle" | "polling" | "resolved" | "timedOut";

interface IUseSignedLienWaiverPollOptions {
    /** Encoded id of the waiver just signed, from the return URL. Undefined when nothing is in flight. */
    signedLienWaiverId: string | undefined;
    data: LienWaiverReviewResponse | undefined;
    onRefresh: () => Promise<void>;
}

/**
 * Waits for a Nutrient-signed waiver's approval to be processed.
 *
 * Approval happens asynchronously via pubsub after the signing completion, so the review list reloaded
 * straight after signing can still show the waiver as pending. This re-fetches on an interval until the
 * waiver leaves the pending state, then stops. It also stops at a timeout or on a failed refresh, in
 * which case the caller tells the sub their approval is still processing rather than showing an error.
 */
export function useSignedLienWaiverPoll({
    signedLienWaiverId,
    data,
    onRefresh,
}: IUseSignedLienWaiverPollOptions) {
    const [state, setState] = useState<SignedLienWaiverPollState>("idle");

    // Read inside the interval without making it a dependency — re-creating the interval on every
    // refreshed payload would restart the timer and the poll would never reach its deadline.
    const dataRef = useRef(data);
    const onRefreshRef = useRef(onRefresh);
    useEffect(() => {
        dataRef.current = data;
        onRefreshRef.current = onRefresh;
    });

    const isStillPending = useCallback(
        () =>
            !!signedLienWaiverId &&
            isLienWaiverStillPending(findLienWaiver(dataRef.current, signedLienWaiverId)),
        [signedLienWaiverId],
    );

    useEffect(() => {
        if (!signedLienWaiverId) {
            setState("idle");
            return;
        }

        // The waiver is already resolved in the payload the page loaded with — the approval beat us here.
        if (!isStillPending()) {
            setState("resolved");
            return;
        }

        setState("polling");

        let cancelled = false;
        const deadline = Date.now() + POLL_TIMEOUT_MS;

        const intervalId = window.setInterval(() => {
            if (cancelled) {
                return;
            }

            void onRefreshRef
                .current()
                .then(() => {
                    if (cancelled) {
                        return;
                    }
                    if (!isStillPending()) {
                        window.clearInterval(intervalId);
                        setState("resolved");
                        return;
                    }
                    if (Date.now() >= deadline) {
                        window.clearInterval(intervalId);
                        setState("timedOut");
                    }
                })
                .catch(() => {
                    if (cancelled) {
                        return;
                    }
                    window.clearInterval(intervalId);
                    setState("timedOut");
                });
        }, POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [signedLienWaiverId, isStillPending]);

    return state;
}
